<template>
  <section
    class="sales-history"
    aria-label="Продажи текущей смены"
  >
    <history-header
      :is-loading="loading"
      :is-opened-working-shift="Boolean(workingShiftId)"
      @refresh="loadSales"
    />

    <history-toolbar
      :search-query.sync="searchQuery"
      :total-price="total"
      :total-sales="sales.length"
    />

    <div
      v-if="loading"
      class="sales-history__state"
      role="status"
    >
      Загружаем продажи…
    </div>

    <div
      v-else-if="error"
      class="sales-history__state"
      role="alert"
    >
      <strong>Не удалось загрузить продажи</strong>
      <span>Попробуйте обновить список.</span>
    </div>

    <div
      v-else-if="!filteredSales.length"
      class="sales-history__state"
      role="status"
    >
      <ShoppingBag :size="28"/>

      <strong>{{ getStatusText }}</strong>
      <span>{{ getStatusDescription }}</span>
    </div>

    <div v-else class="sales-history__list">
      <details
        v-for="sale in filteredSales"
        :key="sale.id"
        class="sales-history__sale"
      >
        <summary>
          <span class="sales-history__receipt">
            <ShoppingBag :size="19" />
          </span>

          <span class="sales-history__identity">
            <strong>
              Продажа <span class="sales-history__id">#{{ sale.id }}</span>
            </strong>

            <span>{{ formatDate(sale.createdAt) }} · Товаров: {{ sale.productCount }}</span>
          </span>

          <span
            v-if="sale.isReturned"
            class="sales-history__payment sales-history__payment-returned"
          >
            <Undo2 :size="14" />

            Возврат
          </span>

          <span
            v-else
            class="sales-history__payment"
          >
            <Wallet :size="14" />

            {{ getTypePay(sale) }}
          </span>

          <strong class="sales-history__amount">{{ priceToFormat(sale.price) }}</strong>

          <ChevronDown
            :size="18"
            class="sales-history__chevron"
          />
        </summary>

        <div class="sales-history__details">
          <div class="sales-history__products">
            <h3>
              Состав покупки <span>{{ sale.productCount }}</span>
            </h3>

            <ul v-if="sale.productsNameWithPrice.length">
              <li
                v-for="(product, index) in sale.productsNameWithPrice"
                :key="index"
              >
                <span class="sales-history__number">{{ index + 1 }}</span>
                <span>{{ product.title }}</span>
                <strong>{{ getProductPrice(product) }}</strong>
              </li>
            </ul>

            <p
              v-else
              class="sales-history__muted"
            >
              Информация о товарах отсутствует
            </p>
          </div>

          <aside class="sales-history__info">
            <dl>
              <div>
                <dt>Способ оплаты</dt>
                <dd>{{ getTypePay(sale) }}</dd>
              </div>

              <template v-if="isMultiPay(sale)">
                <div>
                  <dt>Сумма оплаты по терминалу</dt>
                  <dd>{{ getPayTerminal(sale) }}</dd>
                </div>

                <div>
                  <dt>Сумма оплаты наличными</dt>
                  <dd>{{ getPayCash(sale) }}</dd>
                </div>

                <div>
                  <dt>Сумма оплаты переводом</dt>
                  <dd>{{ getPayCardTransfer(sale) }}</dd>
                </div>
              </template>

              <div>
                <dt>Зарплата с продажи</dt>
                <dd>{{ getSalary(sale) }}</dd>
              </div>
              <div class="sales-history__grand-total">
                <dt>Итого</dt>
                <dd>{{ priceToFormat(sale.price) }}</dd>
              </div>

              <div v-if="!sale.isReturned">
                <button
                  class="return-sale"
                  :disabled="loading"
                  @click="returnSale(sale)"
                >
                  Вернуть продажу
                </button>
              </div>
            </dl>
          </aside>
        </div>
      </details>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import HistoryHeader from '@/components/salesHistory/components/historyHeader'
import HistoryToolbar from '@/components/salesHistory/components/historyToolbar'

import {
  ShoppingBag,
  Wallet,
  ChevronDown,
  Undo2
} from 'lucide-vue'

import { priceToFormat, formatDate } from '@/helper'

export default {
  name: 'SalesHistory',
  components: {
    ShoppingBag,
    Wallet,
    ChevronDown,
    Undo2,
    HistoryHeader,
    HistoryToolbar
  },
  data() {
    return {
      sales: [],
      searchQuery: '',
      loading: false,
      error: false, 
      requestId: 0
    }
  },
  computed: {
    ...mapGetters('workingShift', ['getOpenedWorkingShift']),
    ...mapGetters('settings', ['getDefaultValues']),
    ...mapGetters('product', ['getDataObject']),
    ...mapState('sale', { saleData: 'data' }),
    getStatusText () {
      return this.searchQuery
        ? 'Ничего не найдено'
        : 'Продаж пока нет'
    },
    getStatusDescription () {
      return this.searchQuery
        ? 'Попробуйте другое название товара или номер продажи.'
        : 'Здесь появятся покупки текущей открытой смены.'
    },
    workingShiftId () {
      return this.getOpenedWorkingShift?.id || ''
    },
    filteredSales () {
      const searchQuery = this.searchQuery.trim().toLocaleLowerCase('ru')

      const salesFormatted = this.sales.map(sale => {
        const productsNameWithPrice = sale.productListIdWithPrice.map((productStr) => {
          const product = JSON.parse(productStr)

          return {
            title: sale.listProductObjects.find(({ id }) => id === product.id).title,
            price: product.price,
          }
        })

        return {
          ...sale,
          productsNameWithPrice
        }
      })

      if (!searchQuery) {
        return salesFormatted
      }

      const createSearchedText = ({ code, listProductObjects }) => `${code} ${listProductObjects.map(({ title }) => title).join(' ')}`

      return salesFormatted.filter(sale => createSearchedText(sale).toLocaleLowerCase('ru').includes(searchQuery))
    },
    total () {
      return this.filteredSales.reduce((sum, { price }) => sum + (Number(price) || 0), 0)
    }
  },
  watch: {
    workingShiftId: {
      immediate: true,
      handler (val) {
        if (!val) {
          return
        }

        this.loadSales()
      }
    }
  },
  created () {
    this.$bus.on('update-sale-history', this.loadSales)
  },
  beforeDestroy () {
    this.$bus.on('update-sale-history', this.loadSales)
  },
  methods: {
    ...mapActions('sale', ['loadDataByOpenedWorkingShift', 'return']),
    priceToFormat,
    formatDate,
    getProductPrice (product) {
      return product.price == null
        ? 'Цена не указана'
        : priceToFormat(product.price)
    },
    getSalary (sale) {
      return sale.salary == null ? 'Не указана' : priceToFormat(sale.salary)
    },
    getTypePay ({ typePay }) {
      return (this.getDefaultValues?.['type-pay'] || []).find(({ code }) => code === typePay)?.name || 'Не указан'
    },
    isMultiPay ({ typePay }) {
      return typePay === 'multi'
    },
    toggleLoading () {
      this.loading = !this.loading
    },
    getPayTerminal ({ payTerminal }) {
      return priceToFormat(payTerminal)
    },
    getPayCash ({ payCash }) {
      return priceToFormat(payCash)
    },
    getPayCardTransfer ({ payCardTransfer }) {
      return priceToFormat(payCardTransfer)
    },
    async loadSales () {
      this.toggleLoading()

      const { data, error } = await this.loadDataByOpenedWorkingShift()

      this.toggleLoading()

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка!',
          text: 'Ошибка при загрузке данных продаж!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.sales = data
    },
    async returnSale ({ id }) {
      const { error } = await this.return(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при возврате продажи!',
          text: 'Произошла ошибка при возврате продажи!',
          clean: false,
          duration: 3000
        })

        return
      }

      await this.loadSales()

      this.$notify({
        type: 'success',
        title: 'Продажа успешно возвращена!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
